import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { MaintenanceReportFilterModel } from 'src/app/models/maintenance/maintenance-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { GroupService } from 'src/app/services/archive/group.service';
import { SubgroupService } from 'src/app/services/archive/subgroup.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceReportService } from 'src/app/services/maintenance/maintenance-report.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-maintenance-costs-for-equipment',
  templateUrl: './maintenance-costs-for-equipment.component.html',
  styleUrls: ['./maintenance-costs-for-equipment.component.scss'],
  standalone: false
})
export class MaintenanceCostsForEquipmentComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly articleService = inject(ArticleService);
  private readonly branchService = inject(BranchService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly groupService = inject(GroupService);
  private readonly headerService = inject(HeaderService);
  private readonly maintenanceReportService = inject(MaintenanceReportService);
  private readonly rentalPeriodService = inject(RentalPeriodService);
  private readonly subgroupService = inject(SubgroupService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formMaintenanceReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listGroup: any[] = [];
  listSubgroup: any[] = [];
  filteredSubgroups: any[] = [];
  listArticle: any[] = [];
  listEquipmentLogbook: any[] = [
    { label: 'Todos', value: null },
    { label: 'Con Hoja de Vida', value: 1 },
    { label: 'Sin Hoja de Vida', value: 0 }
  ];
  nameWindow: string = 'Informe de Costos de Mantenimiento por Equipo';
  projectName = ProjectName.projectName;
  companyId: number = 0;
  branchId: number = 0;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  async changeArticle(articleId: number | null) {
    if (articleId != 0 && articleId != null) {
      const response = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.formMaintenanceReportFilter.controls["groupId"].patchValue(response.groupId);
      this.formMaintenanceReportFilter.controls["subgroupId"].patchValue(response.subgroupId);
    } else {
      this.formMaintenanceReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeBranch(branchId: number | null) {
    if (branchId != 0 && branchId != null) {
      this.formMaintenanceReportFilter.controls["groupId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["subgroupId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["articleId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["swEquipmentLogbook"].patchValue(null);
    } else {
      this.formMaintenanceReportFilter.controls["branchId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeGroup(groupId: number | null) {
    this.filterSubgroupByGroupId(groupId);
    if (groupId != 0 && groupId != null) {
      this.formMaintenanceReportFilter.controls["subgroupId"].patchValue(0);
    } else {
      this.formMaintenanceReportFilter.controls["groupId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["subgroupId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeSubgroup(subgroupId: number | null) {
    if (subgroupId != 0 && subgroupId != null) {
      const subgroup = this.listSubgroup.find(x => x.value == subgroupId);
      if (subgroup) {
        this.formMaintenanceReportFilter.controls["groupId"].patchValue(subgroup.groupId);
        this.filterSubgroupByGroupId(subgroup.groupId);
      }
    } else {
      this.formMaintenanceReportFilter.controls["groupId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["subgroupId"].patchValue(0);
    }
    this.changeFilter();
  }

  filterSubgroupByGroupId(groupId: number | null) {
    if (groupId != null && groupId !== 0) {
      this.filteredSubgroups = this.listSubgroup.filter(x => x.groupId === groupId);
      const subgroupId = this.formMaintenanceReportFilter.get("subgroupId")?.value;
      const subgroupExists = this.listSubgroup.some(x => x.groupId === groupId && x.value === subgroupId);

      if (subgroupId && !subgroupExists) {
        this.formMaintenanceReportFilter.controls["subgroupId"].patchValue(0);
      }
    } else {
      this.filteredSubgroups = this.listSubgroup;
    }
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    await this.getFirstBranchByUserAuthenticatedAsync();
    this.filteredSubgroups = this.listSubgroup;
  }

  async exportExcel() {
    if (this.changeFilters) {
      try {
        const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
        const response = await firstValueFrom(this.maintenanceReportService.exportMaintenanceCostsForEquipment(model));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.nameWindow}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para exportar.');
    }
  }

  async getAllLists() {
    try {
      const [branches, groups, subgroups, articles] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.groupService.getAllGroupsForSelectAsync()),
        firstValueFrom(this.subgroupService.getAllSubgroupsForSelectAsync()),
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync())
      ]);
      this.listBranch = branches;
      this.listGroup = groups;
      this.listSubgroup = subgroups;
      this.filteredSubgroups = subgroups;
      this.listArticle = articles;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formMaintenanceReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.branchId = branch.branchId;
      this.companyId = branch.companyId;
      await this.getLastRentalPeriod(branch.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getLastRentalPeriod(branchId: number) {
    try {
      const rentalPeriod = await firstValueFrom(this.rentalPeriodService.getLastRentalPeriod(branchId));
      this.formMaintenanceReportFilter.controls["initialDate"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
      this.formMaintenanceReportFilter.controls["finalDate"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar el periodo de alquiler:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para imprimir.');
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formMaintenanceReportFilter.valid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
    if (model.finalDate < model.initialDate) {
      this.toastHandlerService.showToast('warning', 'La fecha final no puede ser menor a la fecha inicial.');
      return;
    }
    try {
      this.dataReport = await firstValueFrom(this.maintenanceReportService.getMaintenanceCostsForEquipment(model));
      if (this.dataReport && this.dataReport.data?.length > 0) {
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast('info', 'No se encontraron datos.');
      }
    } catch (error) {
      this.changeFilters = false;
      console.error("Error al consultar el reporte:", error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      initialDate: {
        required: 'La fecha inicial es obligatoria.'
      },
      finalDate: {
        required: 'La fecha final es obligatoria.'
      },
      branchId: {
        notNumber: 'Debe seleccionar una Sucursal válida.'
      },
      groupId: {
        notNumber: 'Debe seleccionar un Grupo válido.'
      },
      subgroupId: {
        notNumber: 'Debe seleccionar un Subgrupo válido.'
      },
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      },
      swEquipmentLogbook: {
        notNumber: 'Debe seleccionar una Hoja de Vida válida.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formMaintenanceReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formMaintenanceReportFilter = this.formBuilder.group({
      initialDate: ['', [Validators.required]],
      finalDate: ['', [Validators.required]],
      branchId: [0, [CustomValidators.numberOnlyValidator()]],
      groupId: [0, [CustomValidators.numberOnlyValidator()]],
      subgroupId: [0, [CustomValidators.numberOnlyValidator()]],
      articleId: [0, [CustomValidators.numberOnlyValidator()]],
      swEquipmentLogbook: [null, [CustomValidators.numberOnlyValidator()]]
    });
  }

  validateFormControls() {
    Object.keys(this.formMaintenanceReportFilter.controls).forEach((key) => {
      const control = this.formMaintenanceReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
  }

}
